import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase/client';

export const Route = createFileRoute('/_auth/change-password')({
  component: ChangePasswordPage,
});

const schema = z
  .object({
    password: z.string().min(8, 'Mínimo 8 caracteres'),
    confirm: z.string().min(1),
  })
  .refine((d) => d.password === d.confirm, {
    message: 'Las contraseñas no coinciden',
    path: ['confirm'],
  });

type FormValues = z.infer<typeof schema>;

function ChangePasswordPage() {
  const navigate = useNavigate();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { password: '', confirm: '' },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: values.password,
        data: { must_change_password: false },
      });
      if (error) throw error;
      toast.success('Contraseña actualizada. Bienvenido a MEIDEI.');
      await navigate({ to: '/' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudo actualizar la contraseña';
      toast.error(message);
    }
  });

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-xl font-semibold text-ink">Cambiar contraseña</h1>
        <p className="mt-1 text-sm text-quiet">
          Tu cuenta requiere que establezcas una contraseña nueva antes de continuar.
        </p>
      </div>

      <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="cp-password">Nueva contraseña</Label>
          <Input
            id="cp-password"
            type="password"
            autoComplete="new-password"
            {...form.register('password')}
          />
          {form.formState.errors.password ? (
            <span className="text-xs text-critical">{form.formState.errors.password.message}</span>
          ) : null}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="cp-confirm">Confirmar contraseña</Label>
          <Input
            id="cp-confirm"
            type="password"
            autoComplete="new-password"
            {...form.register('confirm')}
          />
          {form.formState.errors.confirm ? (
            <span className="text-xs text-critical">{form.formState.errors.confirm.message}</span>
          ) : null}
        </div>

        <Button type="submit" disabled={form.formState.isSubmitting}>
          {form.formState.isSubmitting ? 'Guardando...' : 'Establecer contraseña'}
        </Button>
      </form>
    </div>
  );
}
